import { DSRESTClient } from './../shared/communication-objects/restclient.service';
import { api } from './../shared/communication-objects/api-path';
import { Injectable } from '@angular/core';
import { LogService } from './log.service';
import { BehaviorSubject, Observable, catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { CacheBuster, globalCacheBusterNotifier } from 'ts-cacheable';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { TankService } from './tank.service';
import { TranslateService } from '@ngx-translate/core';
import { TankSetupGpsResponse } from '@interfaces/TankGpsFormResponse';
import { AppDialogService } from './app-dialog.service';
import { DropDown } from '@interfaces/drop-down.interface';
import type { IAffiliateOrgPicker } from './organization.service';
import { HttpParams } from '@angular/common/http';
import { deviceTypeCategory } from '../interfaces/deviceTypeCategory';
import type { ITankChangeHistory } from '../interfaces/tank-change-history.interface';
import { coeffExpOrDefault } from '../utilities/coefficient-expansion';

@Injectable({ providedIn: 'root' })
export class TankSetupService {
  private readonly apiURL = api`tank-setup`;

  constructor(
    private httpClient: DSRESTClient,
    private logger: LogService,
    private appDialogService: AppDialogService,
    private tankService: TankService,
    private translate: TranslateService,
  ) {}

  public getTankSetupGeneralVM(uniqueId: number): Observable<any> {
    return this.getTankSetupGeneral(uniqueId);
  }

  /** The affiliate organization picker for one tank, with its current affiliate kept visible. */
  public getAffiliateOrgPicker(uniqueId: number): Observable<IAffiliateOrgPicker> {
    return this.httpClient.getAsModel<IAffiliateOrgPicker>(api`${this.apiURL}/${uniqueId}/affiliate-org-picker`);
  }

  // get uniqueId from route
  public getUniqueIdFromRoute(route: ActivatedRoute): Observable<number> {
    return route.params.pipe(
      map((params) => {
        return params.id;
      }),
    );
  }

  // get tabNo from route
  public getTabNoFromRoute(route: ActivatedRoute): Observable<string> {
    return route.queryParams.pipe(
      map((queryParams) => {
        if (!queryParams) return 'general'; //return General tab by default
        return queryParams.t.toString();
      }),
    );
  }

  reloadTankServiceTab: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
  public refreshTankServiceTab() {
    this.tankService.reloadKeyTankInfo();
    this.reloadTankServiceTab.next();
  }

  @CacheBuster({
    isInstant: true,
  })
  saveAllForms(
    uniqueId: number,
    setupFormGroup: UntypedFormGroup,
    timestamps: {
      tankTimestamp: any;
      tankConfigTimestamp: any;
      deviceTimestamp: any;
      serviceTimestamp: any;
    },
  ): Observable<any> {

    // if this is a client tank and the call schedule has changed, ask for confirmation
    var alarmCallScheduleControl = setupFormGroup.controls.alarmcallschedule;
    if (alarmCallScheduleControl?.dirty && alarmCallScheduleControl?.value.usingHostTank) {

      return this.appDialogService
        .showConfirmationDialog(
          this.translate.instant('TankSetup.CallSchedule.ConfirmTitle'),
          this.translate.instant('TankSetup.CallSchedule.ConfirmMessage'),
        )
        .pipe(
          switchMap((result) => {
            if (!result) return of(null);
            return this.ProcessSaveRequest(uniqueId, setupFormGroup, timestamps);
          }),
        )
    }
    else {
      return this.ProcessSaveRequest(uniqueId, setupFormGroup, timestamps);
    }
  }

  ProcessSaveRequest(
    uniqueId: number,
    setupFormGroup: UntypedFormGroup,
    timestamps: {
      tankTimestamp: any;
      tankConfigTimestamp: any;
      deviceTimestamp: any;
      serviceTimestamp: any;
    },
  ): Observable<any> {

    globalCacheBusterNotifier.next();
    this.showGeneralMessage(`${this.translate.instant('General.Saving')}...`);

    //return if form is NOT dirty (no changes were made)
    if (!setupFormGroup.dirty) {
      return of(null);
    }

    //enable these form controls so they are included in the post
    setupFormGroup?.get('gpsSource')?.get('currentLatitude')?.enable();
    setupFormGroup?.get('gpsSource')?.get('currentLongitude')?.enable();

    //loop through FormGroups and get only dirty formGroups
    const formGroups: object[] = [];
    Object.keys(setupFormGroup.controls).forEach((key) => {
      let values: any = null;

      if (key === 'alarmEmails') {
        //emailAlertFormItems is an array of formGroups
        const fa = setupFormGroup
          .get(key)
          .get('userAlarmDefintions') as UntypedFormArray;

        values = fa.controls
          .filter((item) => {
            return item.dirty;
          })
          .map((item) => {
            const alarmOptions = {};
            //flatten alarmOptions into a single Dictionary<string,bool> object for the post
            const itemVal = item.value;
            itemVal.alarmOptions.forEach((option) => {
              if (!option) return;

              const optionKey = Object.keys(option)[0];
              alarmOptions[optionKey] = option[optionKey];
            });
            return { ...item.value, alarmOptions };
          });

        if (values.length === 0) return;
      }
      else if (key === 'telemetry') {
        //telemetry is a FormGroup
        values = this.processTelemetryForm(setupFormGroup, key);
        if (!values) return;
      }
      else if (key === 'forecastAlarms') {
        //posted as part of 'alarms' below
        return;
      }
      else if (key === 'alarms') {
        //Forecast Alarms is its own panel but the alarms save DTO is a full snapshot (a missing field
        //is written as 0), so both panels always post together as one 'alarms' section
        values = this.processAlarmsForms(setupFormGroup);
        if (!values) return;
      }
      else {
        const control: FormGroup = setupFormGroup.get(key) as FormGroup;
        if (!control.dirty) return;

        values = control.value;
      }

      formGroups[key] = this.sanitizeObjectKeys(values);
    });

    const rtn = this.httpClient
      .post(api`${this.apiURL}/${uniqueId}/v2`, {
        ...formGroups,
        ...timestamps,
      })
      .pipe(
        tap({
          next: () => {
            this.showSuccessMessage(
              `${this.translate.instant('General.Saved')}`,
            );
            this.tankService.reloadKeyTankInfo();
          },
          finalize: () => {
            //disable these form controls again after the post
            setupFormGroup?.get('gpsSource')?.get('currentLatitude')?.disable();
            setupFormGroup
              ?.get('gpsSource')
              ?.get('currentLongitude')
              ?.disable();
          },
        }),
      );

    return rtn;
  }

  private processTelemetryForm(setupFormGroup: UntypedFormGroup, key: string): any {
    const control: FormGroup = setupFormGroup.get(key) as FormGroup;
    if (!control.dirty) return;

    const values = { ...control.value };
    const coeffExp = control.get('coeffExp');
    if (coeffExp?.disabled) values['coeffExp'] = coeffExp.value;
    for (const key of ['tankMinimumConverted', 'sensorOffsetConverted']) {
      if (Object.prototype.hasOwnProperty.call(values, key) && (values[key] == null || values[key] === '')) {
        values[key] = 0;
      }
    }

    //Drain/Fill Detected Volume and Short Drain/Fill Detected Volume are editable in the UI as
    //both a percent and a gal amount; the two are kept in sync client-side (tank-setup-telemetry-form
    //component), so the gal fields are always the source of truth to post - never re-derive from percent here.
    values['fillDetectDeltaConverted'] = Number.parseFloat(values['fillDetectVolumeConverted']);
    values['shortFillDeltaConverted'] = Number.parseFloat(values['shortFillVolumeConverted']);
    delete values['fillDetectVolumeConverted'];
    delete values['shortFillVolumeConverted'];

    //a blank coefficient saves the default; the server reads null as "not sent" and keeps the stored value
    if ('coeffExp' in values) values['coeffExp'] = coeffExpOrDefault(values['coeffExp']);

    return values;
  }

  private processAlarmsForms(setupFormGroup: UntypedFormGroup): any {
    const alarms = setupFormGroup.get('alarms');
    const forecastAlarms = setupFormGroup.get('forecastAlarms');
    if (!alarms?.dirty && !forecastAlarms?.dirty) return;

    const values = { ...alarms.value, ...forecastAlarms?.value };
    for (const key of ['usageToReorderAlarm', 'usageToSafetyStockAlarm']) {
      if (Object.prototype.hasOwnProperty.call(values, key) && (values[key] == null || values[key] === '')) {
        values[key] = 0;
      }
    }
    return values;
  }

  private sanitizeObjectKeys(obj: Record<string, any>): Record<string, any> {
    const sanitizedObj: Record<string, any> = {};

    //if obj is an array, sanitize each item in the array but not the array itself
    if (Array.isArray(obj))
      return obj.map((item) => this.sanitizeObjectKeys(item));

    for (const [key, value] of Object.entries(obj)) {
      const sanitizedKey = key.replace(/[\/]/g, '');

      if (
        typeof value === 'object' &&
        !Array.isArray(value) &&
        value !== null
      ) {
        sanitizedObj[sanitizedKey] = this.sanitizeObjectKeys(value);
      } else {
        sanitizedObj[sanitizedKey] = value;
      }
    }

    return sanitizedObj;
  }

  private getProducts(uniqueId: number): any {
    return this.httpClient.get<any>(
      api`${this.apiURL}/${uniqueId}/general/products`,
    );
  }

  getTankSetupGeneral(uniqueId: number): Observable<any> {
    return this.httpClient.get<any>(api`${this.apiURL}/${uniqueId}/general`);
  }

  getTankSetupReadingsAndTelemetryVM(uniqueId: number): Observable<[ITankSetupTelemetryResponse, ITankSetupCurrentReadingsResponse, ITankSetupAlarmCallScheduleResponse | null]> {
    return forkJoin([
      this.httpClient.getDirect<ITankSetupTelemetryResponse>(
        api`${this.apiURL}/${uniqueId}/telemetry/v2`,
      ),
      this.httpClient.getDirect<ITankSetupCurrentReadingsResponse>(
        api`${this.apiURL}/${uniqueId}/currentReadings`,
      ),
      // The call schedule is an optional add-on to this section. Left to fail, it takes the whole
      // forkJoin down with it and Telemetry (tank type, product, region, route, install status)
      // never renders; null just hides the call schedule form.
      this.getAlarmCallSchedule(uniqueId).pipe(catchError(() => of(null))),
    ]);
  }

  getTankSetupAlarmsVM(uniqueId: number): Observable<any> {
    return forkJoin([
      this.httpClient.get<ITankSetupAlarmsResponse>(
        api`${this.apiURL}/${uniqueId}/alarms`,
      ),
      this.httpClient.get<ITankSetupDeviceAlarmResponse>(
        api`${this.apiURL}/${uniqueId}/devicealarm`,
      ),
    ]);
  }

  getTankSetupGpsVM(uniqueId: number): Observable<TankSetupGpsResponse | null> {
    return this.httpClient.getDirect<TankSetupGpsResponse>(
      api`${this.apiURL}/${uniqueId}/gps/location`,
    );
  }

  /** @deprecated Legacy Audit History; replaced by getTankChangeHistory behind the tankChangeHistory feature flag. */
  getTankSetupCompareConfigsVM(uniqueId: number): Observable<any> {
    return this.httpClient.get<any>(
      api`${this.apiURL}/${uniqueId}/compareConfigs`,
    );
  }

  /** The tank's whole history; the timeline filters and pages it client-side. */
  getTankChangeHistory(uniqueId: number): Observable<ITankChangeHistory> {
    return this.httpClient.getAsModel<ITankChangeHistory>(api`${this.apiURL}/${uniqueId}/change-history`);
  }

  /** The tank's full history, always unfiltered - the timeline's filters are a viewing aid only. */
  exportTankChangeHistory(uniqueId: number): Observable<ArrayBuffer> {
    return this.httpClient.getFile(api`${this.apiURL}/${uniqueId}/change-history/export`);
  }

  getTankSetupTankLinkDataVM(uniqueId: number): Observable<any> {
    return this.httpClient.get<ITankSetupTankLinkDataResponse>(
        api`${this.apiURL}/${uniqueId}/tankLinkData`,
    );
  }

  public getRatePlan(ratePlanId: number): Observable<any> {
    return this.httpClient.get<ITankSetupTankLinkDataResponse>(
      api`${this.apiURL}/${ratePlanId}/getRatePlan`,
    );
  }

  private showGeneralMessage(message: string) {
    this.appDialogService.showGeneralMessage(message);
  }

  private showSuccessMessage(message: string) {
    this.appDialogService.showSuccessMessage(message);
  }

  private showErrorMessage(message: string, response: any) {
    this.appDialogService.showErrorMessage(message, response);
  }

  /**
   * Legacy "Update Now": queues an on-demand reading for the tank's device.
   * Fire and forget - the device answers over cellular on its own schedule and the
   * new reading arrives through the normal cache sync.
   */
  updateNow(uniqueId: number): Observable<any> {
    return this.httpClient.post(api`${this.apiURL}/${uniqueId}/UpdateNow`, {});
  }

  /**
   * Polled while an Update Now request is outstanding. `processed` means CommPktMgr removed the
   * queue row - the point at which the tank's rowversions are stable and the page can re-stamp.
   * It does not mean the device was reached, let alone that it answered.
   */
  getUpdateNowStatus(uniqueId: number): Observable<IUpdateNowStatusResponse> {
    return this.httpClient.getDirect<IUpdateNowStatusResponse>(
      api`${this.apiURL}/${uniqueId}/UpdateNowStatus`,
    );
  }

  GetUnitOfMeasureListByTelemetryType(uniqueId: number): Observable<any> {
    const url = api`${this.apiURL}/${uniqueId}/GetUnitOfMeasureListByTelemetryType`;
    const response = this.httpClient.get<any>(url);
    return response;
  }

  updateTankInventoryUnits(
    uniqueId: number,
    selectedUOMId,
    timestamps: {
      tankTimestamp: any;
      tankConfigTimestamp: any;
      deviceTimestamp: any;
      serviceTimestamp: any;
    },
  ): Observable<any> {
    this.showGeneralMessage(
      `${this.translate.instant('UnitOfMeasure.UpdatingInventoryUnits')}...`,
    );
    const request = { selectedUOMId, ...timestamps };
    return this.httpClient
      .post(api`${this.apiURL}/${uniqueId}/UpdateTankInventoryUnits`, request)
      .pipe(
        tap(
          () => {
            this.showSuccessMessage(
              `${this.translate.instant(
                'UnitOfMeasure.UpdateInventoryUnitsComplete',
              )}`,
            );
          },
          (response) =>
            this.showErrorMessage(
              `${this.translate.instant(
                'UnitOfMeasure.UpdateInventoryUnitsFailed',
              )}`,
              response,
            ),
        ),
      );
  }

  getEmailAlertDefinitions(uniqueId: number): Observable<any> {
    const url = api`${this.apiURL}/${uniqueId}/alarms/email-alert-definitions`;
    const response = this.httpClient.get<any>(url);
    return response;
  }

  emailExistsAlreadyInDb(
    emailAddress: string,
    uniqueId: number,
  ): Observable<number> {
    return this.httpClient
      .postForResponse<{ userId: number }>(
        api`${this.apiURL}/alarms/email-alerts/verify-email`,
        { email: emailAddress, uniqueId: uniqueId },
      )
      .pipe(
        map((response) => {
          if (!response) return 0;

          return response.userId;
        }),
      );
  }

  getAlarmCallSchedule(uniqueId: number): Observable<ITankSetupAlarmCallScheduleResponse> {
    const url = api`${this.apiURL}/${uniqueId}/alarms/alarm-call-schedule`;
    const response = this.httpClient.getDirect<ITankSetupAlarmCallScheduleResponse>(url);
    return response;
  }

  getTankSetupPermissionsVM(uniqueId: number): Observable<any> {
    return this.httpClient.get<any>(api`${this.apiURL}/${uniqueId}/permissions`);
  }

  getPhotos(uniqueId: number): Observable<TankDetailsPhotoDto[]> {
    const response = this.httpClient.getAsModel<TankDetailsPhotoDto[]>(
      api`tank-photos/${uniqueId}`,
    );
    return response.pipe(map((x) => this.sortPhotos(x)));
  }

  uploadPhotos(
    files: Blob[],
    metadata: PhotoUploadDto[],
  ): Observable<TankDetailsPhotoDto[]> {
    const formData = new FormData();
    files.forEach((blob, index) => {
      formData.append(`files`, blob, `file-${index}`);
      formData.append(`metadata-${index}`, JSON.stringify(metadata[index]));
    });
    return this.httpClient.postForResponse<TankDetailsPhotoDto[]>(
      api`tank-photos/${metadata[0].tankId}`,
      formData,
    );
  }

  deletePhoto(uniqueId: number, photoIdId: number): Observable<boolean> {
    let params = new HttpParams()
      .set('photoId', photoIdId.toString())
      .set('uniqueId', uniqueId.toString());
    return this.httpClient.deleteForResponse<boolean>(
      api`tank-photos/delete?${params}`,
    );
  }

  setPhotoAsPrimary(uniqueId: number, photoIdId: number): Observable<boolean> {
    let params = new HttpParams()
      .set('photoId', photoIdId.toString())
      .set('uniqueId', uniqueId.toString());
    return this.httpClient.put<boolean>(
      api`tank-photos/set-primary?${params}`,
      null,
    );
  }



  private sortPhotos(photos: TankDetailsPhotoDto[]) {
    photos.sort((a, b) => {
      // First sort by isPrimary, descending
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;

      // Then sort by index, ascending
      return a.index - b.index;
    });

    return photos;
  }

//  testMobileAppAPIEndpoints(): any {
//    //test the mobile AssignHostDevice API
//    const request = { tankId: "10184177", hostDeviceId: "123456" };
//    const url = `tank-service/AssignHostDevice`;
//    console.log("++++ testMobileAppAPIEndpoints: called")
//    const result = this.httpClient.post(url, request)
//    console.log("++++ testMobileAppAPIEndpoints: result", result)
//    return result;
//  }
}
export interface TankDetailsPhotoDto {
  photoId: number;
  isPrimary: boolean;
  thumbnailUrl: string;
  url: string;
  index: number;
  createdDate: string;
  latitute: number | null;
  longitude: number | null;
}

export interface PhotoUploadDto {
  name: string;
  tankId: number;
  isPrimary: boolean;
  isApproved: boolean;
}

export interface ITankSetupTelemetryResponse extends _ITankSetupFormBase {
  tankId: number;
  route: string;
  region: string;
  routes: DropDown<string>[];
  regions: DropDown<string>[];
  convertedVolumeUnitOfMeasureId: number;
  chartId: number;
  limitCapacityFlag: boolean;
  activationDate: string;
  tankMinimumConverted: number;
  productId: string;
  prodDesc: string;
  specGrav: number;
  coeffExp: number;
  tankNormallyFills: boolean;
  highSetPointConverted: number;
  telemetryTypeId: number;
  products: ProductDropDown<string>[];
  strapCharts: DropDown<number>[];
  tankHgt: number;
  capacityLimit: number;
  tankCapConverted: number;
  capacityLimitConverted: number;
  forecastDailyUsageConverted: number;
  displayVolumeUoM: string;
  displayLengthUoM: string;
  fillDetectDeltaConverted: number;
  lowSetPointConverted: number;
  tankHgtConverted: number;
  shortFillDeltaConverted: number;
  telemetryTypeName: string;
  sensorOffsetConverted: number;
  highSetPointConvertedValueEnabled: boolean;
  lowSetPointConvertedValueEnabled: boolean;
  sensorOffsetConvertedValueEnabled: boolean;
  forecastDailyUsageConvertedValueEnabled: boolean;
  minimumDailyUsageConverted: number;
  isProductListCustom: boolean;
  userProductNumber: string;
  useUltraSonicRange: boolean;
  tankName: string;
  radarMaxSensorRangeConverted: number;
  installStatusTypeId?: number;
  statusChange?: string;
  installStatusTypes?: DropDown<string>[];
}

export interface ITankSetupAlarmsResponse extends _ITankSetupFormBase {
  capacityLimitConverted: number;

  lowLevel: number;
  highLevel: number;
  shortFillDeltaConverted: number;

  lowLowLevel: number;
  highHighLevel: number;
  fillDetectDeltaConverted: number;

  lowTemp: number;
  highTemp: number;
  volumeDelta: number;

  hasExpectedCallAlarm: boolean;
  expectedCallInterval: number;
  rateChangeDelta: number;

  tankMinimumConverted: number;
  reorderUsage: number;
  forecastDailyUsageConverted: number;
  safetyStockUsage: number;

  // units of measure
  convertedVolumeUnitOfMeasureId: number;
  displayVolumeUoM: string;
  displayLengthUoM: string;

  //=== tank data ===
  thirtyFiveDayAvgConverted: number;
  thirtyFiveDayAvgInvCount: number;
  sixtyDayAvgConverted: number;
  sixtyDayAvgInvCount: number;
  ninetyDayAvgConverted: number;
  ninetyDayAvgInvCount: number;
  isPtTank: boolean;
  isReplicate: boolean;

  //=== device data ===
  usesCallSchedule: boolean;
}

export interface ITankSetupDeviceAlarmResponse extends _ITankSetupFormBase {
  //=== tank config data ===
  wakeInterval: number;
  dataLogDelta: number;
  deviceFillDetect: boolean;
  deviceFillDetectDelta: number;
  usageDelta: number;
  deviceSuspiciousFilter: boolean;
  deviceSuspiciousDelta: number;
  deviceFillHysteresis: number;

  deviceLowLevel: boolean;
  deviceHighLevel: boolean;
  deviceCriticalLowLevel: boolean;
  criticalHighAlarm: boolean;
  deviceCriticalHighLevel: boolean;
  deviceUsageAlarm: boolean;

  // units of measure
  convertedVolumeUnitOfMeasureId: number;
  displayVolumeUoM: string;
  displayLengthUoM: string;

  // === device data ===
  hasAlarmSetPoints: boolean;
  hasDataLogging: boolean;
  hasTankSetup1: boolean;

  // === device type data ===
  hasTodp: number;
  isClientDevice: number;
  deviceCategory: deviceTypeCategory;
}
export interface ITankSetupAlarmCallScheduleResponse
  extends _ITankSetupFormBase {
  startTimeHour: number;
  callsPerDay: number;
  callInterval: number;
  activeDaysBitValue: number;
  isPTTank: boolean;
  isSuperUser: boolean;
  usingHostTank: boolean;
  hostTankConfigId: number;
  hostTankConfigTimestamp: string;
  usesCallSchedule?: boolean;
  deviceCategory: deviceTypeCategory;
}


export interface IUpdateNowStatusResponse {
  processed: boolean;
}

export interface ITankSetupCurrentReadingsResponse extends _ITankSetupFormBase {
  invTime: Date;
  grossLevelConverted: number;
  grossVolumeConverted: number;
  manuallyModified: boolean;
  displayVolumeUoM: string;
  displayLengthUoM: string;
  unitOfMeasureId: number;
  convertedVolumeUnitOfMeasureId: number;
  rxPktSequence: number;
  txPktSequence: number;
  diagTimeStamp: Date | null;
  /** Device is flagged for on-demand readings - gates the "Update Now" button. */
  onDemandReading: boolean;
  /** Child/compartment tank - excluded from "Update Now". */
  isChildTank: boolean;
}

export interface ITankSetupTankLinkDataResponse extends _ITankSetupFormBase {
  convertedVolumeUnitOfMeasureId: number;
    // tank data
    isAffiliateTank: boolean;
    isReplicaTank: boolean;

    // tank config data
    servicePlanId: number;
    ratePlanId: number;
    startTime: Date;
    expectedCallInterval: number;
    enableLocation: boolean;
    diagCallDayMask: number;
    enableLocationCheckbox: boolean;

    // device data
    hasLocation: boolean;
    targetConfigFlags: number;
    rtuSensorWarmTime: number;
    hasEditableWarmTime: boolean;
    hasCallSchedule: boolean;

    // device type data
    isClientDevice: boolean;
    isSkyTankSensor: boolean;
    isConnectedToGateway: boolean;

    // rate plan data
    monthlyFee: number;
    ratePlanName: string;

    // service plan dropdown
    servicePlanDropDown: DropDown<number>[];

    // rate plan dropdown
    ratePlanDropDown: DropDown<number>[];
    enableRatePlanDropdown: boolean;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export interface _ITankSetupFormBase {
  tankTimestamp: string;
  tankConfigTimestamp: string;
  deviceTimestamp: string;
}

export interface ProductDropDown<T> extends DropDown<T> {
  specGrav: number;
  prodDesc: string;
  coeffExp: number;
  userProductNumber: string;
}

export const DaysOfWeekBitValues = [1, 2, 4, 8, 16, 32, 64];

export const HoursOfDayText = [
  '12 AM',
  '1 AM',
  '2 AM',
  '3 AM',
  '4 AM',
  '5 AM',
  '6 AM',
  '7 AM',
  '8 AM',
  '9 AM',
  '10 AM',
  '11 AM',
  '12 PM',
  '1 PM',
  '2 PM',
  '3 PM',
  '4 PM',
  '5 PM',
  '6 PM',
  '7 PM',
  '8 PM',
  '9 PM',
  '10 PM',
  '11 PM',
];

export enum TankSetupNavTabs {
  General,
  RtuDeviceSetup,
  ConfigCompare,
  CurrentReadingsAndTelemetry,
  Alarms,
  Permissions,
  GpsCoordinatesSource,
  Photos,
  Audit,
  Service,
  TankLinkData
};

